/**
 * The plain, zod-free half of the LLM contract.
 *
 * `schemas.ts` is the source of truth for *validation*, and validation costs 78 kB of
 * Zod. A handful of the values beside it are not schemas at all — they are frozen
 * tables and a closed vocabulary — and they are read on the client's hot path: the
 * settings sheet lists `MOVEMENT_KINDS`, every branch request slices the path to
 * `BRANCH_PATH_LIMIT`, and every session starts by zeroing a count per movement kind.
 *
 * While those lived in `schemas.ts` a client bundle could not drop Zod, because keeping
 * one constant meant keeping the module that declared it and therefore the whole schema
 * graph. They live here instead, in a module that imports nothing. `schemas.ts`
 * re-exports every name below, so no import site had to change; new client-side code
 * should prefer this module directly, and the *only* thing that belongs here is a value
 * that needs no Zod to express.
 */

/**
 * The kind of harmonic move a suggestion represents.
 *
 * This vocabulary is closed on purpose: it is what `takenKinds` / `ignoredKinds`
 * counting is built on, and a free-text label could not be counted.
 */
export const MOVEMENT_KINDS = [
  'functional-dominant',
  'functional-subdominant',
  'relative',
  'parallel',
  'modal',
  'chromatic-mediant',
  'borrowed',
  'common-tone',
  'secondary-dominant',
  'deceptive',
  'planing',
  'distant-modulation',
  'stylistic-idiom',
] as const;

export type MovementKind = (typeof MOVEMENT_KINDS)[number];

/** The maximum path length the client should send, and the server should accept. */
export const BRANCH_PATH_LIMIT = 16;

/**
 * Cap on the one-line steer a "something else — but distant" command carries into the
 * next branch request. Long enough for a spoken phrase, short enough that it cannot
 * become a second intention: the steer is for one request, not for the session.
 */
export const REGENERATE_HINT_LIMIT = 120;

/**
 * The models the player can choose between, in-app. These are user-facing tier names,
 * not provider model IDs — the server maps them to the real OpenAI strings, so a
 * provider rename touches exactly one file. The provider is OpenAI by decision
 * (owner, 2026-08-12), which retired the earlier "no model names anywhere" rule in
 * favour of an in-app switch.
 */
export const MODEL_CHOICES = ['sol', 'terra', 'luna'] as const;
export type ModelChoice = (typeof MODEL_CHOICES)[number];

/**
 * Prose limits from the brief. Enforced by `BranchSuggestionRuntimeSchema` and applied
 * by `clampBranchSuggestion`; expressed to the model through `.describe()` because
 * `maxLength` is not in the Structured Outputs strict subset.
 */
export const BRANCH_SUGGESTION_LIMITS = Object.freeze({
  relationLabel: 30,
  shortDescription: 140,
  voicingHint: 100,
  ambiguityNote: 120,
  styleTags: 3,
});
